/**
 * Counts down to the next dispatch cutoff in a fixed timezone (UK by default),
 * regardless of where the visitor's own clock is set.
 *
 * The cutoff is a wall-clock time in that zone, so it cannot be expressed as a
 * fixed UTC offset — the UK moves between GMT and BST. Intl.DateTimeFormat is
 * used to read "what time is it right now in London", and the difference between
 * that reading and the real UTC clock gives the zone's current offset.
 */
if (!customElements.get('delivery-countdown')) {
  class DeliveryCountdown extends HTMLElement {
    connectedCallback() {
      this.timezone = this.dataset.timezone || 'Europe/London';
      this.cutoffHour = Number(this.dataset.cutoffHour ?? 14);
      this.cutoffMinute = Number(this.dataset.cutoffMinute ?? 0);
      this.timers = this.querySelectorAll('[data-timer]');

      this.formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });

      this.onVisibilityChange = () => {
        // A background tab does not need a per-second timer. Resume with an
        // immediate tick so the display is never stale when the tab comes back.
        if (document.hidden) {
          this.stop();
        } else {
          this.tick();
          this.start();
        }
      };

      this.tick();
      this.start();
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    start() {
      this.stop();
      if (!document.hidden) this.intervalId = window.setInterval(() => this.tick(), 1000);
    }

    stop() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    /**
     * The zone's wall clock at `instant`, read as though it were UTC, minus the
     * real UTC instant — which leaves the zone's offset in force at that moment.
     * Milliseconds are stripped because formatToParts only resolves to the
     * second; without that the offset carries up to 999ms of error.
     */
    zoneOffsetAt(instant) {
      const parts = this.formatter.formatToParts(instant);
      const get = (type) => Number(parts.find((part) => part.type === type)?.value || 0);
      const wallClock = Date.UTC(
        get('year'),
        get('month') - 1,
        get('day'),
        get('hour'),
        get('minute'),
        get('second')
      );
      return wallClock - (instant.getTime() - instant.getMilliseconds());
    }

    getRemaining() {
      const now = new Date();
      const parts = this.formatter.formatToParts(now);
      const get = (type) => Number(parts.find((part) => part.type === type)?.value || 0);

      const year = get('year');
      const month = get('month');
      const day = get('day');

      // Date.UTC normalises overflow, so day + 1 rolls month and year correctly.
      const asUtc = (y, m, d, h, min, s) => Date.UTC(y, m - 1, d, h, min, s);

      const zoneNowAsUtc = asUtc(year, month, day, get('hour'), get('minute'), get('second'));

      let cutoffAsUtc = asUtc(year, month, day, this.cutoffHour, this.cutoffMinute, 0);
      const pastCutoff = zoneNowAsUtc >= cutoffAsUtc;
      if (pastCutoff) {
        cutoffAsUtc = asUtc(year, month, day + 1, this.cutoffHour, this.cutoffMinute, 0);
      }

      // Resolve the cutoff twice. The first pass uses the offset in force *now*,
      // which is wrong on the eve of a clock change: on 28 March the countdown to
      // the next day's 14:00 would run an hour long, because tomorrow is BST and
      // today is GMT. Re-reading the offset at the estimated instant corrects it.
      const estimate = cutoffAsUtc - this.zoneOffsetAt(now);
      const cutoffUtc = cutoffAsUtc - this.zoneOffsetAt(new Date(estimate));

      let diff = Math.max(0, cutoffUtc - now.getTime());
      const hours = Math.floor(diff / 3600000);
      diff -= hours * 3600000;
      const minutes = Math.floor(diff / 60000);
      diff -= minutes * 60000;

      return { hours, minutes, seconds: Math.floor(diff / 1000), pastCutoff };
    }

    tick() {
      const { hours, minutes, seconds, pastCutoff } = this.getRemaining();
      const pad = (value) => String(value).padStart(2, '0');
      const label = `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;

      for (const el of this.timers) el.textContent = label;

      // Drives the before/after copy swap in the block's stylesheet.
      this.dataset.pastCutoff = pastCutoff ? 'true' : 'false';
    }
  }

  customElements.define('delivery-countdown', DeliveryCountdown);
}
